import type { ProjectModelDraft } from '../project/model';
import type { SymbolGraphic } from './model';
import { effectiveSymbols } from '../styles/effectiveSymbols';
import { reconcileAnnotationLayerLegend } from '../annotations/legend';

export type SymbolTarget =
  | { readonly kind: 'point'; readonly layerId: string; readonly entry?: number | 'other' | 'null' }
  | { readonly kind: 'marker'; readonly layerId: string; readonly annotationId: string };
export function targetGraphic(
  project: ProjectModelDraft,
  target: SymbolTarget,
): SymbolGraphic | undefined {
  if (target.kind === 'marker') {
    const a = project.annotationLayers
      .find((l) => l.id === target.layerId)
      ?.annotations.find((a) => a.id === target.annotationId);
    return a?.type === 'marker' ? a.style.shape : undefined;
  }
  const layer = project.layers.find((l) => l.id === target.layerId);
  if (!layer) return undefined;
  if (target.entry === undefined) return layer.style.symbols.point.shape;
  const c = layer.style.classification;
  const e =
    c.mode === 'categorised'
      ? target.entry === 'other'
        ? c.other
        : typeof target.entry === 'number'
          ? c.categories[target.entry]?.symbol
          : undefined
      : c.mode === 'graduated'
        ? target.entry === 'null'
          ? c.nullSymbol
          : typeof target.entry === 'number'
            ? c.classes[target.entry]?.symbol
            : undefined
        : undefined;
  return e ? effectiveSymbols(layer.style.symbols, e).point.shape : undefined;
}
export function projectWithTargetGraphic(
  project: ProjectModelDraft,
  target: SymbolTarget,
  graphic: Exclude<SymbolGraphic, string>,
): ProjectModelDraft {
  if (target.kind === 'marker')
    return {
      ...project,
      annotationLayers: project.annotationLayers.map((l) =>
        l.id !== target.layerId
          ? l
          : reconcileAnnotationLayerLegend({
              ...l,
              annotations: l.annotations.map((a) =>
                a.id === target.annotationId && a.type === 'marker'
                  ? { ...a, style: { ...a.style, shape: graphic } }
                  : a,
              ),
            }),
      ),
    };
  return {
    ...project,
    layers: project.layers.map((l) => {
      if (l.id !== target.layerId) return l;
      const style = l.style;
      if (target.entry === undefined)
        return {
          ...l,
          style: {
            ...style,
            symbols: { ...style.symbols, point: { ...style.symbols.point, shape: graphic } },
          },
        };
      const patch = <
        T extends { readonly override?: import('../styles/model').VectorSymbolOverrides },
      >(
        e: T,
      ): T => ({
        ...e,
        override: { ...e.override, point: { ...e.override?.point, shape: graphic } },
      });
      const c = style.classification;
      if (c.mode === 'categorised')
        return {
          ...l,
          style: {
            ...style,
            classification: {
              ...c,
              categories: c.categories.map((e, i) =>
                i === target.entry ? { ...e, symbol: patch(e.symbol) } : e,
              ),
              other: target.entry === 'other' ? patch(c.other) : c.other,
            },
          },
        };
      if (c.mode === 'graduated')
        return {
          ...l,
          style: {
            ...style,
            classification: {
              ...c,
              classes: c.classes.map((e, i) =>
                i === target.entry ? { ...e, symbol: patch(e.symbol) } : e,
              ),
              nullSymbol: target.entry === 'null' ? patch(c.nullSymbol) : c.nullSymbol,
            },
          },
        };
      return l;
    }),
  };
}
